import React from 'react'
import styled, { css } from 'styled-components'

const sizes = {
  xs: css`
    height: 26px;
    padding: 0 8px;
    font-size: 12px;
    border-radius: ${({ theme }) => theme?.radii?.sm || '6px'};
    gap: 4px;
  `,
  sm: css`
    height: 32px;
    padding: 0 12px;
    font-size: 13px;
    gap: 6px;
  `,
  md: css`
    height: 38px;
    padding: 0 16px;
    font-size: 14px;
  `,
  lg: css`
    height: 46px;
    padding: 0 22px;
    font-size: 15px;
  `
}

// Legacy alias: "small" → "sm"
const sizeAlias = { small: 'sm', medium: 'md', large: 'lg' }

const variants = {
  primary: css`
    background: ${({ theme }) => theme.colors.primary};
    color: white;
    border: 1px solid transparent;
    box-shadow: ${({ theme }) => theme.shadow.sm};
    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.primaryHover};
      box-shadow: ${({ theme }) => theme.shadow.md};
    }
  `,
  secondary: css`
    background: ${({ theme }) => theme.colors.secondary};
    color: white;
    border: 1px solid transparent;
    box-shadow: ${({ theme }) => theme.shadow.sm};
    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.secondaryHover};
      box-shadow: ${({ theme }) => theme.shadow.md};
    }
  `,
  danger: css`
    background: ${({ theme }) => theme.colors.danger};
    color: white;
    border: 1px solid transparent;
    box-shadow: ${({ theme }) => theme.shadow.sm};
    &:hover:not(:disabled) {
      background: #B71C1C;
      box-shadow: ${({ theme }) => theme.shadow.md};
    }
  `,
  ghost: css`
    background: transparent;
    color: ${({ theme }) => theme.colors.text};
    border: 1px solid ${({ theme }) => theme.colors.border};
    box-shadow: none;
    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.border}33;
      border-color: ${({ theme }) => theme.colors.muted}66;
    }
  `,
  soft: css`
    background: ${({ theme }) => theme.colors.primary}0D;
    color: ${({ theme }) => theme.colors.primary};
    border: 1px solid ${({ theme }) => theme.colors.primary}22;
    box-shadow: none;
    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.primary}1A;
      border-color: ${({ theme }) => theme.colors.primary}44;
    }
  `
}

function resolveSize(s) {
  return sizeAlias[s] || s || 'md'
}

const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  border-radius: ${({ theme }) => theme.radii.md};
  font-weight: 550;
  letter-spacing: 0.01em;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;
  ${(p) => sizes[p.$size || 'md']};
  ${(p) => variants[p.$variant || 'primary']};
  width: ${(p) => (p.$full ? '100%' : 'auto')};
  opacity: ${(p) => (p.disabled ? 0.5 : 1)};
  pointer-events: ${(p) => (p.disabled ? 'none' : 'auto')};
  &:active:not(:disabled) { transform: translateY(1px); }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`

export function Button({ children, variant, size = 'md', fullWidth = false, disabled, onClick, type = 'button', ...props }) {
  return (
    <StyledButton type={type} onClick={onClick} disabled={disabled} $variant={variant} $size={resolveSize(size)} $full={fullWidth} {...props}>
      {children}
    </StyledButton>
  )
}
